/**
 * What happens when the API says 401.
 *
 * The shared client already tried a single transparent refresh; by the time this
 * handler runs, the session is genuinely gone — idle past five days, signed out
 * elsewhere, or never there (US-01-005.3/.17, US-00-006.10/.11).
 *
 * Three things then have to happen, in this order:
 *
 *   1. remember where the Expert was, so OTP re-verification returns them to it
 *      rather than to the dashboard (US-01-001.22, US-01-005.24)
 *   2. drop the local half of the session
 *   3. EMPTY THE QUERY CACHE — the panel holds other people's tax notices, and a
 *      cache that outlives the session would render them to whoever signs in
 *      next on that browser (US-00-006.1)
 *
 * Installed once from the entry point. It is deliberately not a hook: the client
 * calls it from a fetch rejection, where there is no React tree to be inside of.
 *
 * STEP 3 IS DELAYED TO A MACROTASK, DELIBERATELY (see the user web app's
 * ../../../web/src/lib/unauthenticated.ts for the full account). This handler
 * runs synchronously from inside the shared client's response handling, before
 * the 401 has finished becoming the `ApiError` the query that triggered it is
 * about to reject with. `queryClient.clear()` destroys every Query object,
 * including that one still in flight; destroying it first leaves the rejection
 * with nowhere to land, and the screen reading it — often the very query this
 * 401 came from — is stuck on its loading state rather than ever reaching the
 * login screen. `setTimeout(…, 0)` guarantees the rejection is already an error
 * in the cache before the cache is torn down.
 */

import { setUnauthenticatedHandler } from "@anthra/platform/api"
import { logger } from "@anthra/platform/logger"
import { endSession, holdDestination } from "@anthra/platform/session"
import { type QueryClient } from "@tanstack/react-query"

import { useSessionStore } from "@/stores/use-session-store"

/**
 * Wires the 401 handler.
 *
 * `currentPath` is supplied by the router rather than read from `location` here,
 * so the held destination is the app's own route — including its search
 * parameters — and not whatever the address bar happens to show mid-navigation.
 */
export function installUnauthenticatedHandler(
	queryClient: QueryClient,
	currentPath: () => string,
): void {
	setUnauthenticatedHandler(() => {
		// The store flip happens synchronously and first. Several queries can
		// fail with 401 in the same tick; flipping here makes the second and
		// later ones a no-op instead of three concurrent sign-out sequences.
		if (useSessionStore.getState().status === "signed_out") return
		useSessionStore.getState().markSignedOut()

		void endUnauthenticatedSession(queryClient, currentPath())
	})
}

async function endUnauthenticatedSession(queryClient: QueryClient, path: string): Promise<void> {
	try {
		const held = await holdDestination(path)
		if (held) useSessionStore.getState().setDestination(path)
	} catch (error) {
		// Failing to remember a destination costs the person a tap, not a session.
		logger.warn("could not hold the post-sign-in destination", {}, error)
	}

	await endSession()
	// See the module doc comment: lets the triggering query's rejection land
	// before the cache that would hold it is destroyed.
	await new Promise<void>(resolve => setTimeout(resolve, 0))
	queryClient.clear()
}
